/**
 * Normalize raw / Mongoose-style schema maps into form fields + Formik initial values.
 *
 * Handles:
 * - Dotted nested paths (`notification_settings.email`) → grouped object fields
 * - Nested `items` maps from jsonSafeMongooseSchema
 * - Existing `fields` / `content.fields` / `children`
 * - Prefix-aware nullify (`notification_settings` hides `notification_settings.*`)
 * - Type/ref normalization (Boolean → boolean switch, etc.)
 */

export type NormalizedSchemaField = {
  key: string;
  title: string;
  type: string;
  ref?: string;
  validation?: Record<string, unknown>;
  content?: Record<string, unknown>;
  fields?: NormalizedSchemaField[];
  label?: string;
  description?: string;
  placeholder?: string;
  conditional?: unknown;
  dependsOn?: unknown;
  enum?: unknown;
  default?: unknown;
  [extra: string]: unknown;
};

type NormalizeOptions = {
  nullify?: readonly string[] | null;
};

const PARTICIPANT_KEYS = new Set(['participants', 'attendees']);

/** True when `key` or any of its dotted ancestors is listed in nullify. */
export const isSchemaKeyNullified = (
  key: string,
  nullify?: readonly string[] | null
): boolean => {
  if (!key || !nullify?.length) return false;
  if (nullify.includes(key)) return true;
  return nullify.some(
    (n) => Boolean(n) && (key === n || key.startsWith(`${n}.`))
  );
};

const humanizeKey = (key: string): string => {
  const leaf = key.includes('.') ? key.split('.').pop() || key : key;
  return leaf
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Canonical UI type/ref values that native handleFieldType matches case-sensitively
 * (web lowercases). Prefer these when the schema only has `type` and no mongoose `ref`.
 */
const UI_TYPE_CANONICAL: Record<string, { type: string; ref: string }> = {
  dataset: { type: 'dataSet', ref: 'dataSet' },
  keywordlookup: { type: 'keywordLookup', ref: 'keywordLookup' },
  keyword_lookup: { type: 'keywordLookup', ref: 'keywordLookup' },
  dropdown: { type: 'dropDown', ref: 'dropDown' },
  datetime: { type: 'dateTime', ref: 'dateTime' },
  daterange: { type: 'dateRange', ref: 'dateRange' },
  textarea: { type: 'textArea', ref: 'textArea' },
  checkbox: { type: 'checkbox', ref: 'checkbox' },
  checkgroup: { type: 'checkGroup', ref: 'checkGroup' },
  radiobutton: { type: 'radioButton', ref: 'radioButton' },
  multicheck: { type: 'multicheck', ref: 'multicheck' },
  image: { type: 'image', ref: 'image' },
  images: { type: 'images', ref: 'images' },
  file: { type: 'file', ref: 'file' },
  location: { type: 'location', ref: 'location' },
  city: { type: 'city', ref: 'city' },
  address: { type: 'address', ref: 'address' },
  slider: { type: 'slider', ref: 'slider' },
  tags: { type: 'tags', ref: 'tags' },
  barcode: { type: 'barcode', ref: 'barcode' },
  color: { type: 'color', ref: 'color' },
  email: { type: 'email', ref: 'email' },
  password: { type: 'password', ref: 'password' },
  phone: { type: 'phone', ref: 'phone' },
  mobile: { type: 'mobile', ref: 'mobile' },
  currency: { type: 'currency', ref: 'currency' },
  price: { type: 'price', ref: 'price' },
  participants: { type: 'participants', ref: 'participants' },
};

const normalizeTypeAndRef = (
  field: Record<string, unknown>
): { type: string; ref: string } => {
  const rawType = String(field.type ?? field.ref ?? 'string');
  const rawRef = field.ref != null ? String(field.ref) : '';
  const typeLower = rawType.toLowerCase();
  const refLower = rawRef.toLowerCase();

  if (
    typeLower === 'boolean' ||
    typeLower === 'bool' ||
    typeLower === 'switch' ||
    refLower === 'boolean' ||
    refLower === 'switch'
  ) {
    return { type: 'boolean', ref: 'boolean' };
  }
  if (typeLower === 'checkbox' || refLower === 'checkbox') {
    return { type: 'checkbox', ref: 'checkbox' };
  }
  if (
    typeLower === 'number' ||
    typeLower === 'int' ||
    typeLower === 'integer' ||
    typeLower === 'float' ||
    typeLower === 'double' ||
    refLower === 'number'
  ) {
    return { type: 'number', ref: rawRef || 'number' };
  }
  if (typeLower === 'date' || refLower === 'date') {
    return { type: 'date', ref: rawRef || 'date' };
  }
  if (typeLower === 'datetime' || refLower === 'datetime') {
    return { type: 'dateTime', ref: rawRef || 'dateTime' };
  }
  if (typeLower === 'daterange' || refLower === 'daterange') {
    return { type: 'dateRange', ref: rawRef || 'dateRange' };
  }
  if (
    typeLower === 'object' ||
    typeLower === 'embedded' ||
    typeLower === 'mixed' ||
    refLower === 'object' ||
    refLower === 'group'
  ) {
    // Keep explicit object/group refs; Mixed alone is not always an object group.
    if (refLower === 'group' || refLower === 'object' || typeLower === 'object') {
      return { type: 'object', ref: rawRef || 'object' };
    }
  }
  if (
    typeLower === 'array' ||
    Array.isArray(field.type) ||
    refLower === 'array' ||
    refLower === 'tags' ||
    refLower === 'participants'
  ) {
    return {
      type: rawRef || (typeLower === 'array' ? 'array' : rawType),
      ref: rawRef || (typeLower === 'array' ? 'array' : undefined) || 'array',
    };
  }

  // Explicit mongoose / relational refs win (user_stores, team, customer_datas, …)
  if (rawRef) {
    return { type: rawType || rawRef, ref: rawRef };
  }

  const canonical = UI_TYPE_CANONICAL[typeLower];
  if (canonical) {
    return canonical;
  }

  // Mongoose "String" / "ObjectID" without a UI ref → string input
  if (typeLower === 'objectid' || typeLower === 'string') {
    return { type: 'string', ref: 'string' };
  }

  // Preserve explicit UI types. handleFieldType prefers `ref` over `type`,
  // so keep them aligned — otherwise dataSet fields become plain text inputs.
  return { type: rawType || 'string', ref: rawType || 'string' };
};

const defaultValueForField = (field: NormalizedSchemaField): unknown => {
  if (field.default !== undefined) return field.default;
  const t = String(field.ref || field.type || '').toLowerCase();
  if (t === 'boolean' || t === 'switch' || t === 'checkbox') return false;
  if (t === 'number') return 0;
  if (t === 'date' || t === 'datetime') return new Date();
  if (
    t === 'array' ||
    t === 'tags' ||
    t === 'participants' ||
    t === 'attendees'
  ) {
    return [];
  }
  if (t === 'object' || t === 'group') {
    const nested: Record<string, unknown> = {};
    for (const child of field.fields || []) {
      nested[child.key] = defaultValueForField(child);
    }
    return nested;
  }
  return '';
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  Boolean(v) && typeof v === 'object' && !Array.isArray(v);

/** Build content so array option lists and object shapes both survive normalization. */
const buildFieldContent = (
  raw: Record<string, unknown>,
  label: string
): Record<string, unknown> => {
  if (Array.isArray(raw.content)) {
    // Legacy shape: content: ['A','B'] — promote to enum object used by dropdowns
    return {
      enum: raw.content,
      label: (typeof raw.label === 'string' && raw.label) || label,
    };
  }
  if (isPlainObject(raw.content)) {
    return {
      ...raw.content,
      label:
        (typeof raw.content.label === 'string' && raw.content.label) || label,
    };
  }
  if (typeof raw.label === 'string' && raw.label) {
    return { label: raw.label };
  }
  return { label };
};

const extractNestedMap = (
  field: Record<string, unknown>
): Record<string, unknown> | null => {
  if (isPlainObject(field.items)) return field.items;
  if (isPlainObject(field.schema)) return field.schema;
  if (Array.isArray(field.fields) && field.fields.length === 0) return null;
  return null;
};

const existingNestedFields = (
  field: Record<string, unknown>
): unknown[] | null => {
  if (Array.isArray(field.fields) && field.fields.length) return field.fields;
  const content = field.content;
  if (isPlainObject(content) && Array.isArray(content.fields) && content.fields.length) {
    return content.fields as unknown[];
  }
  if (Array.isArray(field.children) && field.children.length) return field.children;
  return null;
};

const toLeafField = (
  key: string,
  raw: Record<string, unknown>,
  options?: { forceLabel?: string }
): NormalizedSchemaField => {
  const { type, ref } = normalizeTypeAndRef(raw);
  const label =
    (typeof raw.label === 'string' && raw.label) ||
    (isPlainObject(raw.content) && typeof raw.content.label === 'string'
      ? raw.content.label
      : undefined) ||
    options?.forceLabel ||
    humanizeKey(key);

  const content = buildFieldContent(raw, label);

  const nestedFromArray = existingNestedFields(raw);
  const nestedFromMap = extractNestedMap(raw);

  let fields: NormalizedSchemaField[] | undefined;
  let finalType = type;
  let finalRef = ref;

  if (nestedFromArray) {
    fields = nestedFromArray
      .map((child, index) => {
        if (!isPlainObject(child)) return null;
        const childKey = String(
          child.key || child.title || child.name || `field${index}`
        );
        return toLeafField(childKey, child);
      })
      .filter(Boolean) as NormalizedSchemaField[];
    finalType = 'object';
    finalRef = 'object';
  } else if (nestedFromMap) {
    fields = Object.entries(nestedFromMap)
      .filter(([, v]) => isPlainObject(v))
      .map(([childKey, childVal]) =>
        toLeafField(childKey, childVal as Record<string, unknown>)
      );
    finalType = 'object';
    finalRef = 'object';
  }

  const field: NormalizedSchemaField = {
    ...raw,
    key,
    title: typeof raw.title === 'string' ? raw.title : key,
    type: finalType,
    ref: finalRef,
    validation:
      isPlainObject(raw.validation)
        ? (raw.validation as Record<string, unknown>)
        : raw.required
          ? { required: true }
          : undefined,
    content,
    label,
    description:
      typeof raw.description === 'string' ? raw.description : undefined,
    placeholder:
      typeof raw.placeholder === 'string' ? raw.placeholder : undefined,
    conditional: raw.conditional,
    dependsOn: raw.dependsOn,
    enum: raw.enum,
    default: raw.default,
  };

  if (fields?.length) {
    field.fields = fields;
    field.content = {
      ...content,
      fields,
      label: content.label || humanizeKey(key),
      defaultExpanded: content.defaultExpanded ?? false,
    };
  }

  return field;
};

type TreeNode = {
  key: string;
  raw?: Record<string, unknown>;
  children: Map<string, TreeNode>;
  order: number;
};

const ensureChild = (
  parent: TreeNode,
  segment: string,
  order: number
): TreeNode => {
  let node = parent.children.get(segment);
  if (!node) {
    node = { key: segment, children: new Map(), order };
    parent.children.set(segment, node);
  }
  return node;
};

const treeToField = (node: TreeNode): NormalizedSchemaField => {
  if (node.children.size === 0) {
    return toLeafField(node.key, node.raw || { type: 'string' });
  }

  const childFields = [...node.children.values()]
    .sort((a, b) => a.order - b.order)
    .map(treeToField);

  const label =
    (typeof node.raw?.label === 'string' && node.raw.label) ||
    (isPlainObject(node.raw?.content) &&
    typeof (node.raw!.content as Record<string, unknown>).label === 'string'
      ? String((node.raw!.content as Record<string, unknown>).label)
      : undefined) ||
    humanizeKey(node.key);

  return {
    ...(node.raw || {}),
    key: node.key,
    title: node.key,
    type: 'object',
    ref: 'object',
    fields: childFields,
    label,
    content: {
      ...(isPlainObject(node.raw?.content) ? (node.raw!.content as object) : {}),
      label,
      fields: childFields,
      defaultExpanded: false,
    },
  };
};

const applyParticipantsSpecialCase = (
  key: string,
  field: NormalizedSchemaField
): NormalizedSchemaField => {
  const normalizedKey = key.toLowerCase();
  if (!PARTICIPANT_KEYS.has(normalizedKey) && field.ref !== 'participants') {
    return field;
  }
  const label = normalizedKey === 'attendees' ? 'Attendees' : 'Participants';
  return {
    ...field,
    type: 'participants',
    ref: 'participants',
    content: {
      ...(field.content || {}),
      label: field.content?.label || label,
      participant: true,
      multiple: true,
    },
    label: field.label || label,
  };
};

/**
 * Convert a schema map into ordered form fields and nested Formik initial values.
 */
export const normalizeSchemaEntries = (
  schema: Record<string, unknown> | null | undefined,
  options: NormalizeOptions = {}
): { fields: NormalizedSchemaField[]; initialValues: Record<string, unknown> } => {
  if (!schema || typeof schema !== 'object') {
    return { fields: [], initialValues: {} };
  }

  const nullify = options.nullify || [];
  const root: TreeNode = { key: '', children: new Map(), order: -1 };
  let orderCounter = 0;

  const ingest = (path: string, raw: Record<string, unknown>) => {
    if (!path || isSchemaKeyNullified(path, nullify)) return;

    // Expand nested maps in place (items/schema) as dotted children when present
    // alongside treating the parent as an object container.
    const nestedMap = extractNestedMap(raw);
    const existingFields = existingNestedFields(raw);
    const segments = path.split('.').filter(Boolean);

    if (segments.length === 0) return;

    let node = root;
    for (const segment of segments) {
      node = ensureChild(node, segment, orderCounter++);
    }
    node.raw = { ...(node.raw || {}), ...raw };

    if (nestedMap && !existingFields) {
      for (const [childKey, childVal] of Object.entries(nestedMap)) {
        if (!isPlainObject(childVal)) continue;
        ingest(`${path}.${childKey}`, childVal as Record<string, unknown>);
      }
      // Parent becomes a pure container; avoid duplicating items as a leaf type
      node.raw = {
        ...node.raw,
        type: 'object',
        ref: 'object',
      };
      delete (node.raw as { items?: unknown }).items;
      delete (node.raw as { schema?: unknown }).schema;
    }
  };

  for (const [key, value] of Object.entries(schema)) {
    if (!key || key === '__v') continue;
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      // Rare: array schema value used for participants
      if (Array.isArray(value) && PARTICIPANT_KEYS.has(key.toLowerCase())) {
        if (isSchemaKeyNullified(key, nullify)) continue;
        ingest(key, {
          type: 'participants',
          ref: 'participants',
          content: {
            label: key.toLowerCase() === 'attendees' ? 'Attendees' : 'Participants',
            participant: true,
            multiple: true,
          },
        });
      }
      continue;
    }

    const raw = value as Record<string, unknown>;
    // Prefer explicit title as path when present (pre-shaped schema items)
    const path =
      typeof raw.title === 'string' && raw.title && !key.includes('.')
        ? (typeof raw.key === 'string' && raw.key ? raw.key : raw.title)
        : key;

    ingest(path, raw);
  }

  const fields = [...root.children.values()]
    .sort((a, b) => a.order - b.order)
    .filter((node) => !isSchemaKeyNullified(node.key, nullify))
    .map((node) => applyParticipantsSpecialCase(node.key, treeToField(node)));

  const initialValues: Record<string, unknown> = {};
  for (const field of fields) {
    initialValues[field.key] = defaultValueForField(field);
  }

  return { fields, initialValues };
};
